<script lang="ts">
import { computed, defineComponent, watch } from 'vue';

import { CdsFunctionalButton } from '@components/components';

import { makeSizeProps, makeThemeProps, makeTitleProps, provideTheme, useSize } from '../../composable';
import { CdsTransitionExpandX } from '../../transitions';

import type { PropType } from 'vue';

export const componentName = 'CdsSidebar';
export default defineComponent({
  name: componentName,
  components: {
    CdsFunctionalButton,
    CdsTransitionExpandX
  },
  inheritAttrs: false,
  props: {
    /**
     * Показать/Скрыть
     */
    modelValue: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    /**
     * Скрывать overlay в открытом состоянии
     */
    disabledOverlay: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    ...makeSizeProps(),
    ...makeTitleProps(),
    ...makeThemeProps()
  },
  emits: {
    /**
     * Событие изменения модели
     * @type { boolean }
     */
    'update:modelValue': (value: boolean) => true,
    /**
     * Клик вне боковой панели в открытом состоянии
     */
    'click:outside': (event: MouseEvent) => true
  },
  setup(props, { attrs, slots, emit, expose }) {
    const { themeClasses } = provideTheme(props);
    const { sizeClasses } = useSize(props, componentName);

    const showOverlay = computed(() => !props.disabledOverlay && props.modelValue);

    /**
     * Метод закрытия боковой панели
     *
     * @method hide
     */
    const hide = () => {
      emit('update:modelValue', false);
    };

    /**
     * Метод открытия боковой панели
     *
     * @method open
     */
    const show = () => {
      emit('update:modelValue', true);
    };

    const onClickOutside = (event: MouseEvent) => {
      emit('click:outside', event);
    };

    watch(
      () => props.modelValue,
      (value) => {
        value ? show() : hide();
      }
    );

    expose({
      show,
      hide
    });

    return {
      attrs,
      sizeClasses,
      themeClasses,
      showOverlay,
      hasToolbarSlot: computed((): boolean => !!slots.toolbar),
      hasAppendSlot: computed((): boolean => !!slots.append),
      hide,
      onClickOutside
    };
  }
});
</script>

<template>
  <cds-transition-expand-x>
    <div v-if="modelValue" v-bind="attrs" class="cds-sidebar" :class="[sizeClasses, themeClasses]">
      <div class="cds-sidebar__header">
        <h5 class="cds-sidebar__header--title">{{ title }}</h5>
        <cds-functional-button icon="remove" @click="hide" />
      </div>
      <div v-if="hasToolbarSlot" class="cds-sidebar__toolbar">
        <!--
      @slot Слот для тулбара
        @type html|vue
    -->
        <slot name="toolbar" />
      </div>
      <div class="cds-sidebar__content">
        <!--
      @slot Слот для контента
        @type html|vue
    -->
        <slot />
      </div>
      <div v-if="hasAppendSlot" class="cds-sidebar__append">
        <!--
       @slot Слот для подвала
         @type html|vue
     -->
        <slot name="append" />
      </div>
    </div>
  </cds-transition-expand-x>
  <div v-if="showOverlay" class="cds-sidebar__overlay" @click="onClickOutside" />
</template>

<style lang="scss">
@import './CdsSidebar';
</style>
